import {
  CheckCircleTwoTone,
  EditOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';

import { type LeadCardContactMethodData } from '@app/components/LeadCard/data';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';

import { UserContactPhoneModal } from './UserContactPhoneModal';
import {
  isEditablePhoneItem,
  useUserContactMethods,
} from './useUserContactMethods';

import type { FC, ReactNode } from 'react';

import './UserContactMethods.scss';

const CONTACT_ICON_PX_SIZE = 18;

interface UserContactMethodsProps {
  items: LeadCardContactMethodData[];
}

const hasDisplayableValue = ({ value }: LeadCardContactMethodData): boolean =>
  value.trim().length > 0;

const renderContactIcon = ({
  kind,
  socialType,
}: LeadCardContactMethodData): ReactNode => {
  if (kind === 'phone') {
    return <PhoneOutlined className="UserContactMethods__icon" aria-hidden />;
  }

  if (kind === 'email' && !socialType) {
    return <MailOutlined className="UserContactMethods__icon" aria-hidden />;
  }

  return (
    <SocialIntegrationIcon
      type={socialType}
      size={CONTACT_ICON_PX_SIZE}
      className="UserContactMethods__icon"
    />
  );
};

export const UserContactMethods: FC<UserContactMethodsProps> = ({ items }) => {
  const {
    canEditPhones,
    mainPhoneId,
    editingPhoneId,
    editingPhone,
    isPhoneLoading,
    isPhoneSaving,
    startPhoneEditHandler,
    cancelPhoneEditHandler,
    savePhoneHandler,
  } = useUserContactMethods();
  const displayItems = items.filter(hasDisplayableValue);

  if (!displayItems.length) return null;

  return (
    <section className="UserContactMethods">
      <ul className="UserContactMethods__list">
        {displayItems.map((item) => (
          <li className="UserContactMethods__row" key={item.id}>
            <span className="UserContactMethods__icon-wrapper" aria-hidden>
              {renderContactIcon(item)}
            </span>
            <span className="UserContactMethods__content">
              {item.href ? (
                <a
                  className="UserContactMethods__value"
                  href={item.href}
                  target={item.kind === 'social' ? '_blank' : undefined}
                  rel={
                    item.kind === 'social' ? 'noopener noreferrer' : undefined
                  }
                  title={item.value}
                >
                  {item.value}
                </a>
              ) : (
                <span className="UserContactMethods__value" title={item.value}>
                  {item.value}
                </span>
              )}
              {item.kind === 'phone' && item.isVerified && (
                <Tooltip title="Номер подтвержден">
                  <CheckCircleTwoTone
                    className="UserContactMethods__verified-icon"
                    twoToneColor="#52c41a"
                    aria-label="Номер подтвержден"
                  />
                </Tooltip>
              )}
              {canEditPhones && isEditablePhoneItem(item) && (
                <Button
                  type="text"
                  size="small"
                  className="UserContactMethods__edit"
                  icon={<EditOutlined />}
                  aria-label="Изменить телефон"
                  data-testid={`lead-client-${item.id}-edit`}
                  onClick={() => void startPhoneEditHandler(item.id)}
                />
              )}
            </span>
          </li>
        ))}
      </ul>
      <UserContactPhoneModal
        isOpen={editingPhoneId !== null}
        initialPhone={editingPhone}
        isLoading={isPhoneLoading}
        isSaving={isPhoneSaving}
        isRequired={editingPhoneId === mainPhoneId}
        onSave={(phone) => void savePhoneHandler(phone)}
        onCancel={cancelPhoneEditHandler}
      />
    </section>
  );
};
